/**
 * Custom hook for managing visualization scales and grid calculation
 * Handles the heavy computation of coordinate transformation and interpolation
 *
 * When server-computed interpolated_data is available, skips client-side
 * grid interpolation and uses the precomputed grid directly.
 */

import { useMemo } from 'react';
import * as d3 from 'd3';
import { findMinMaxCoordinates, getScales, calculateGrid } from 'src/utils/visualization';
import type { Polygon, InterpolatedData } from 'src/types';

export interface ScalesAndGrid {
  xScale: d3.ScaleLinear<number, number> | null;
  yScale: d3.ScaleLinear<number, number> | null;
  gridValues: number[] | null;
  gridNx: number;
  gridNy: number;
}

export interface UseVisualizationScalesOptions {
  points: any[];
  polygons: Polygon[];
  width: number;
  height: number;
  gridResolution: number;
  interpolatedData?: InterpolatedData | null;
}

export interface UseVisualizationScalesReturn {
  scalesAndGrid: ScalesAndGrid;
  isLoading: boolean;
}

export function useVisualizationScales({
  points,
  polygons,
  width,
  height,
  gridResolution,
  interpolatedData,
}: UseVisualizationScalesOptions): UseVisualizationScalesReturn {
  const scalesAndGrid = useMemo<ScalesAndGrid>(() => {
    if (interpolatedData?.grid_values) {
      // Use server-computed domain and grid — no client-side interpolation
      console.debug(
        '[useVisualizationScales] Using SERVER grid:',
        `${interpolatedData.grid_nx}×${interpolatedData.grid_ny}`,
        `(${interpolatedData.grid_values.length} values)`
      );
      const domains = {
        xDomain: interpolatedData.x_domain,
        yDomain: interpolatedData.y_domain,
      };
      const { xScale, yScale } = getScales(domains, width, height);
      return {
        xScale,
        yScale,
        gridValues: interpolatedData.grid_values,
        gridNx: interpolatedData.grid_nx,
        gridNy: interpolatedData.grid_ny,
      };
    }

    // Fallback: client-side computation (for old data without interpolated_data)
    console.warn(
      '[useVisualizationScales] FALLBACK: client-side IDW interpolation.',
      'interpolatedData received:',
      interpolatedData
    );
    const domains = findMinMaxCoordinates(polygons, points);
    const { xScale, yScale } = getScales(domains, width, height);
    const gridValues = calculateGrid(points, gridResolution, xScale, yScale);

    return {
      xScale,
      yScale,
      gridValues,
      gridNx: gridResolution + 1,
      gridNy: gridResolution + 1,
    };
  }, [points, polygons, width, height, gridResolution, interpolatedData]);

  const isLoading = false;

  return { scalesAndGrid, isLoading };
}
